import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { identifySpUser } from '@/lib/analytics'
import { clearSurveyTenantContext } from '@/lib/survey-tenant-context'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { setCsrfToken } from '@/api/http/interceptors'
import type { ApiResponse } from '@/api/http/types'
import { useSessionStore, type ProviderRole } from '@/stores/useSessionStore'
import { useEkycPromptStore } from '@/stores/useEkycPromptStore'
import { IdentityAuthPath } from '../lib/constants'

const publicConfig = { skipUnauthorizedRedirect: true } as const

export interface IdentityMeUser {
  id: string
  email: string
  name: string
  role: string
  status: string
  subdomain?: string | null
  csrf_token?: string
  firstName?: string | null
  lastName?: string | null
  passwordRequired?: boolean
  requiresPasswordReset?: boolean
  guest?: boolean
  surveyPending?: boolean
  webSurveyRegistrationTypeEnabled?: boolean
}

export interface ForceResetStartData {
  requiresPasswordReset: boolean
  sessionId?: string | null
  maskedEmail?: string | null
  otpExpiresAt?: string | null
}

export interface LoginLockoutData {
  retryAfterSeconds?: number
  lockedUntil?: string
}

export interface VerifyOtpSuccessData {
  resetToken: string
  expiresAt: string
}

/** Contract PasswordResetRequestData — maskedEmail null = unknown email (anti-enumeration path). */
export interface PasswordResetRequestData {
  maskedEmail?: string | null
  otpExpiresAt?: string | null
  resetLinkExpiresAt?: string | null
  canRetryAfter?: number | null
}

/**
 * Roles that identity may return for Signal Provider users.
 * `PARTICIPANT` is the legacy value; both map to `SIGNAL_PROVIDER` in this app.
 */
const VALID_PROVIDER_ROLES = new Set(['SIGNAL_PROVIDER', 'PARTICIPANT'])

function mapProviderRole(): ProviderRole {
  return 'SIGNAL_PROVIDER'
}

/** Identity auth success `loggedUser` (web login / sign-up verify). */
export interface AuthLoggedUserPayload {
  id: string
  email: string
  role: string
  status: string
  firstName?: string | null
  lastName?: string | null
}

export interface LoginSuccessData {
  expiresIn?: number
  loggedUser: AuthLoggedUserPayload
}

/** Seed Zustand from login body when HttpOnly cookies are not yet visible to the next XHR. */
export function applyLoggedUserToSession(
  loggedUser: AuthLoggedUserPayload,
  emailFallback?: string
) {
  // Guard: only apply if identity confirms a provider role.
  if (!VALID_PROVIDER_ROLES.has(loggedUser.role)) {
    return
  }

  const setUser = useSessionStore.getState().setUser
  const displayName =
    [loggedUser.firstName, loggedUser.lastName].filter(Boolean).join(' ') ||
    emailFallback ||
    loggedUser.email

  setUser({
    id: loggedUser.id,
    email: emailFallback ?? loggedUser.email,
    name: displayName,
    role: mapProviderRole(),
    subdomain: null,
  })
}

/** True only when API explicitly requests set-password (e.g. login without password). */
export function signupVerifyNeedsPasswordSetup(
  data:
    | Pick<
        SignupVerifySuccessData,
        'passwordSetupRequired' | 'redirectTo' | 'platform'
      >
    | null
    | undefined
): boolean {
  if (!data) return false
  if (data.passwordSetupRequired === false) return false
  return (
    data.passwordSetupRequired === true || data.redirectTo === '/set-password'
  )
}

/** Best-effort clear of host-only auth cookies (e.g. Sensemaker jar on shared localhost). */
async function clearForeignSessionCookies(): Promise<void> {
  try {
    await apiClient.post(IdentityAuthPath.LOGOUT, undefined, publicConfig)
  } catch {
    // Cookie may already be invalid for this portal — ignore.
  }
  setCsrfToken(null)
}

export async function fetchAndApplySession(): Promise<IdentityMeUser> {
  const { setUser, setPasswordSetupRequired, setRequiresForceReset } =
    useSessionStore.getState()

  const { data: envelope } = await apiClient.get<ApiResponse<IdentityMeUser>>(
    IdentityAuthPath.ME,
    publicConfig
  )

  const raw = envelope.data

  // Reject Sensemaker / platform admin users from this portal.
  // Cookies on localhost are not port-scoped, so a Sensemaker session cookie
  // will be sent here — we must refuse it (fail-closed) and clear the jar so
  // the next SP login can set provider cookies.
  if (!VALID_PROVIDER_ROLES.has(raw.role)) {
    await clearForeignSessionCookies()
    throw new Error(
      'WRONG_APP: Sensemaker sessions are not valid in Signal Provider portal'
    )
  }

  if (raw.csrf_token) {
    setCsrfToken(raw.csrf_token)
  }

  const displayName =
    [raw.firstName, raw.lastName].filter(Boolean).join(' ') ||
    raw.name ||
    raw.email

  const guest = raw.guest === true || raw.status === 'GUEST'
  const surveyPending =
    raw.surveyPending === true || Boolean(raw.id?.startsWith('pending:'))

  setUser({
    id: raw.id,
    email: raw.email,
    name: displayName,
    role: mapProviderRole(),
    subdomain: raw.subdomain ?? null,
    guest,
    surveyPending,
    webSurveyRegistrationTypeEnabled: raw.webSurveyRegistrationTypeEnabled === true,
  })

  // PostHog cannot merge two identified ids, so guest / pending sessions stay
  // anonymous until the real Responder id is identified after signup.
  if (!guest && !surveyPending && !raw.id?.includes(':')) {
    posthog.identify(raw.id, { name: displayName, role: mapProviderRole() })
    identifySpUser(raw.id)
  }

  const pendingInitialPassword =
    useSessionStore.getState().pendingInitialPasswordSetup
  if (raw.passwordRequired === true && !pendingInitialPassword) {
    setPasswordSetupRequired(true)
  } else if (raw.passwordRequired === false) {
    setPasswordSetupRequired(false)
  }

  if (raw.requiresPasswordReset === true) {
    setRequiresForceReset(true)
  } else if (raw.requiresPasswordReset === false) {
    setRequiresForceReset(false)
  }

  return raw
}

/** Hydrate Zustand from HttpOnly cookies when the store is still empty (e.g. instrument link return visit). */
export async function ensureSpSessionHydrated(): Promise<boolean> {
  if (useSessionStore.getState().isAuthenticated) {
    return true
  }
  try {
    await fetchAndApplySession()
    return useSessionStore.getState().isAuthenticated
  } catch {
    return false
  }
}

export function useMe() {
  const explicitLogout = useSessionStore((s) => s.explicitLogout)

  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchAndApplySession,
    retry: false,
    enabled: !explicitLogout,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      email: string
      password: string
      remember_me: boolean
    }) => {
      const email = body.email.trim().toLowerCase()
      const { data } = await apiClient.post<ApiResponse<LoginSuccessData>>(
        IdentityAuthPath.LOGIN,
        {
          email,
          password: body.password,
          remember_me: body.remember_me,
          platform: 'web',
        },
        publicConfig
      )
      if (data.data?.loggedUser) {
        applyLoggedUserToSession(data.data.loggedUser, email)
      }

      // Prove HttpOnly cookies before treating login as complete. Body-only
      // Zustand sessions look logged-in until refresh, then /me 401 → /login.
      await new Promise<void>((resolve) => {
        queueMicrotask(() => resolve())
      })
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        useSessionStore.getState().clearSession()
        setCsrfToken(null)
        throw new Error('Login session could not be established')
      }

      return data.data
    },
  })
}

export function useSetInitialPassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      new_password: string
      confirm_password: string
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ passwordUpdated?: boolean; redirectTo?: string }>
      >(IdentityAuthPath.SP_SET_PASSWORD, body)
      return data.data
    },
    onSuccess: async () => {
      useSessionStore.getState().clearPendingInitialPasswordSetup()
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        // Password set; next navigation may re-fetch /me.
      }
    },
  })
}

export interface SignupVerifySuccessData {
  platform: string
  expiresIn?: number
  loggedUser: AuthLoggedUserPayload
  passwordSetupRequired?: boolean
  redirectTo?: string
}

export function useVerifySignupOtp() {
  return useMutation({
    mutationFn: async (body: { signupSessionId: string; code: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<SignupVerifySuccessData>
      >(
        IdentityAuthPath.SP_SIGNUP_VERIFY,
        {
          signupSessionId: body.signupSessionId,
          code: body.code,
          platform: 'web',
        },
        publicConfig
      )
      return data.data
    },
    onSuccess: (data) => {
      if (data?.loggedUser) {
        applySignupVerifySession(data)
      }
      // Do not fetch /me here — it races RequireGuest (isError + clearSession) before navigate.
      // Home route will hydrate /me after navigation; verify body already has loggedUser.
    },
  })
}

export function useResendSignupOtp() {
  return useMutation({
    mutationFn: async (body: { signupSessionId: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ signupSessionId: string; otpExpiresAt?: string }>
      >(IdentityAuthPath.SP_SIGNUP_RESEND, body, publicConfig)
      return data.data
    },
  })
}

/** Apply signup verify session — home first; force /set-password when session TTL elapses. */
export function applySignupVerifySession(
  data: SignupVerifySuccessData,
  emailFallback?: string
) {
  const expiresIn = data.expiresIn ?? 3600
  useSessionStore.getState().markPendingInitialPasswordSetup(expiresIn)
  if (data.loggedUser) {
    applyLoggedUserToSession(data.loggedUser, emailFallback)
  }
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<PasswordResetRequestData>
      >(IdentityAuthPath.PASSWORD_RESET_REQUEST, body, publicConfig)
      return data.data
    },
  })
}

export function useVerifyPasswordResetOtp() {
  return useMutation({
    mutationFn: async (body: { email: string; code: string }) => {
      const { data } = await apiClient.post<ApiResponse<VerifyOtpSuccessData>>(
        IdentityAuthPath.PASSWORD_RESET_VERIFY_OTP,
        body,
        publicConfig
      )
      return data.data
    },
  })
}

export function useResendPasswordResetOtp() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      await apiClient.post<ApiResponse<unknown>>(
        IdentityAuthPath.PASSWORD_RESET_RESEND_OTP,
        body,
        publicConfig
      )
    },
  })
}

export function useValidateResetToken(token: string | null) {
  return useQuery({
    queryKey: queryKeys.auth.validateResetToken(token ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<{ valid?: boolean; expired?: boolean }>
      >(`${IdentityAuthPath.PASSWORD_RESET_VALIDATE}/${token}`, publicConfig)
      return data
    },
    enabled: !!token,
    retry: false,
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (body: {
      token: string
      new_password: string
      confirm_password: string
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ passwordUpdated?: boolean; redirectTo?: string }>
      >(IdentityAuthPath.PASSWORD_RESET, body, publicConfig)
      return data.data
    },
  })
}

export function useForceResetStart() {
  return useMutation({
    mutationFn: async (body: { email: string }) => {
      const { data } = await apiClient.post<ApiResponse<ForceResetStartData>>(
        IdentityAuthPath.SP_WEB_FORCE_RESET_START,
        { email: body.email.trim().toLowerCase() },
        publicConfig
      )
      return data.data
    },
  })
}

export interface ForceResetOtpVerifyData {
  requiresPasswordReset: boolean
}

export function useForceResetOtpVerify() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: { sessionId: string; code: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<ForceResetOtpVerifyData>
      >(IdentityAuthPath.SP_WEB_FORCE_RESET_OTP_VERIFY, body, publicConfig)
      return data.data
    },
    onSuccess: async () => {
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        // Cookie session may need a brief moment to propagate; caller handles navigation.
      }
    },
  })
}

export function useForceResetOtpResend() {
  return useMutation({
    mutationFn: async (body: { sessionId: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ sessionId: string; otpExpiresAt?: string | null }>
      >(IdentityAuthPath.SP_WEB_FORCE_RESET_OTP_RESEND, body, publicConfig)
      return data.data
    },
  })
}

export function useApplyForceReset() {
  return useMutation({
    mutationFn: async (body: {
      newPassword: string
      confirmPassword: string
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<{ passwordUpdated: boolean }>
      >(IdentityAuthPath.SP_WEB_FORCE_RESET_APPLY, body)
      return data.data
    },
    // No /me refetch — apply bumps tokenVersion and invalidates the OTP cookie session.
    // ForceResetPasswordPage clears client state; RequireAuth redirects to /login.
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const clearSession = useSessionStore((s) => s.clearSession)

  return useMutation({
    mutationFn: async () => {
      await apiClient.post<ApiResponse<unknown>>(IdentityAuthPath.LOGOUT)
    },
    onSettled: () => {
      posthog.capture('user_logged_out')
      posthog.reset()
      setCsrfToken(null)
      clearSurveyTenantContext()
      clearSession()
      // Drop any pending Verify / Prerequisites open so login can't revive them.
      useEkycPromptStore.getState().reset()
      queryClient.clear()
      navigate('/login', { replace: true })
    },
  })
}
